import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  ClockIcon, 
  ChatBubbleLeftIcon,
  PaperAirplaneIcon,
  MicrophoneIcon,
  VideoCameraIcon,
  SparklesIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { submitAnswerRequest, selectUploadPending } from '@store/slices/interviewSlice';
import MediaRecorder from '@components/Media/MediaRecorder';

const CHUNKED_UPLOAD_MIN_BYTES = 5 * 1024 * 1024; // 5 MB

const QuestionCard = ({ 
  question, 
  sessionToken, 
  progress, 
  answerFormat, 
  mediaPermissionGranted = false,
  companyName = 'the hiring company'
}) => {
  const dispatch = useDispatch();
  const uploadPending = useSelector(selectUploadPending);
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [answerStartTime] = useState(Date.now());
  const textareaRef = useRef(null);

  // Timer state - use answer_before from question if available
  const getInitialTimeLeft = () => {
    // Check if question has answer_before timestamp
    if (question?.answer_before) {
      const deadline = new Date(question.answer_before).getTime();
      const now = new Date().getTime();
      const secondsLeft = Math.floor((deadline - now) / 1000);
      return secondsLeft > 0 ? secondsLeft : 0;
    }
    
    // Fallback to time_limit_seconds or default 5 minutes
    const timeLimit = question?.time_limit_seconds || 
                     question?.time_remaining_seconds || 
                     300; // 5 minutes default
    return timeLimit > 0 ? timeLimit : 300;
  };
  
  const [timeLeft, setTimeLeft] = useState(getInitialTimeLeft());
  const [timerExpired, setTimerExpired] = useState(false);
  const timerRef = useRef();

  // Reset state when question changes
  useEffect(() => {
    const newTimeLeft = getInitialTimeLeft();
    setTimeLeft(newTimeLeft);
    setTimerExpired(false);
    setAnswer('');
    setIsSubmitting(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setTimerExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [question?.id]);

  // Auto-resize textarea

  // Detect if this is the last question
  const isLastQuestion = progress &&
    (progress.answered_questions + 1) === progress.total_questions;
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.max(200, textareaRef.current.scrollHeight) + 'px';
    }
  }, [answer]);

  // Submit text answer
  const handleSubmitAnswer = async () => {
    if (!answer.trim()) {
      return;
    }
    
    setIsSubmitting(true);
    const answerDuration = Math.floor((Date.now() - answerStartTime) / 1000);
    
    try {
      dispatch(submitAnswerRequest({
        sessionToken,
        questionId: question.id,
        answerText: answer.trim(),
        durationSeconds: answerDuration,
        answerType: 'text',

      }));
      
      // Reset state - saga will handle moving to next question
      setAnswer('');
    } catch (error) {
      console.error('Error submitting answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit media answer (video/audio)
  const handleMediaSubmit = async (mediaData) => {
    setIsSubmitting(true);
    const answerDuration = Math.floor((Date.now() - answerStartTime) / 1000);
    
    try {
      const formData = new FormData();
      formData.append('question_id', question.id);
      formData.append('answer_text', mediaData.transcription || 'Media response provided');
      formData.append('answer_type', answerFormat);
      formData.append('duration_seconds', answerDuration.toString());
      
      if (mediaData.transcription_method) {
        formData.append('transcription_method', mediaData.transcription_method);
      }
      
      // Attach media files
      if (mediaData.videoBlob) {
        formData.append('video', mediaData.videoBlob, 'answer.webm');
      }
      if (mediaData.audioBlob) {
        formData.append('audio', mediaData.audioBlob, 'answer.webm');
      }

      const mediaBlob = mediaData.videoBlob || mediaData.audioBlob;
      const mediaKind = mediaData.videoBlob ? 'video' : 'audio';
      const useChunkedUpload = !!mediaBlob && mediaBlob.size >= CHUNKED_UPLOAD_MIN_BYTES;

      if (useChunkedUpload) {
        dispatch(submitAnswerRequest({
          sessionToken,
          questionId: question.id,
          answerText: mediaData.transcription || 'Media response provided',
          durationSeconds: answerDuration,
          answerType: answerFormat,
          chunkedUpload: {
            enabled: true,
            blob: mediaBlob,
            mediaKind,
            fileName: 'answer.webm',
            mimeType: mediaBlob.type || (mediaKind === 'video' ? 'video/webm' : 'audio/webm'),
          },
        }));
        return;
      }
      
      dispatch(submitAnswerRequest({
        sessionToken,
        formData, // Pass FormData directly
      }));
      
    } catch (error) {
      console.error('Error submitting media answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format timer display
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate circular timer stroke
  const getTimerStroke = () => {
    const totalTime = question?.time_limit_seconds || 300;
    const percent = (timeLeft / totalTime) * 100;
    const circumference = 2 * Math.PI * 54; // radius = 54
    return circumference - (percent / 100) * circumference;
  };

  // Timer color based on time left
  const getTimerColor = () => {
    const totalTime = question?.time_limit_seconds || 300;
    const percent = (timeLeft / totalTime) * 100;
    
    if (percent > 50) return 'text-green-500';
    if (percent > 25) return 'text-amber-500';
    if (percent > 10) return 'text-orange-500';
    return 'text-red-500';
  };

  // Can submit check
  const canSubmit = (
    (answerFormat === 'text' && answer.trim().length > 0) || 
    (answerFormat !== 'text')
  ) && !isSubmitting && !timerExpired;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Async upload indicator — informational only, never blocks progression */}
      {uploadPending && (
        <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-700">
          <ArrowUpTrayIcon className="w-4 h-4 animate-bounce flex-shrink-0" />
          <span>Uploading your last response in the background…</span>
        </div>
      )}
      {/* Circular Timer */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600">
          Question {((progress?.answered_questions || 0) + 1)} of {progress?.total_questions || 0}
        </div>
        
        <div className="relative w-28 h-28">
          <svg className="transform -rotate-90 w-28 h-28">
            {/* Background circle */}
            <circle
              cx="56"
              cy="56"
              r="54"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              className="text-gray-200"
            />
            {/* Progress circle */}
            <circle
              cx="56"
              cy="56"
              r="54"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              strokeDasharray={2 * Math.PI * 54}
              strokeDashoffset={getTimerStroke()}
              className={`transition-all duration-1000 ${getTimerColor()}`}
              strokeLinecap="round"
            />
          </svg>
          {/* Time display */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <ClockIcon className={`w-5 h-5 mx-auto mb-1 ${getTimerColor()}`} />
              <div className={`text-lg font-bold ${getTimerColor()}`}>
                {formatTimer(timeLeft)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
        {/* Question Header with Number */}
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
            Question {question?.display_order || 1} of {progress?.total_questions || 1}
          </div>
        </div>

        {/* Question Type Badge */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <ChatBubbleLeftIcon className="w-4 h-4 text-indigo-500" />
            <span className="text-sm font-medium text-gray-600">
              {question?.question_type ? 
                question.question_type.charAt(0).toUpperCase() + question.question_type.slice(1).replace('_', ' ') 
                : 'Question'}
            </span>
          </div>
          
          {answerFormat !== 'text' && (
            <div className="flex items-center space-x-2 px-3 py-1 bg-purple-50 rounded-full">
              {answerFormat === 'video' ? (
                <VideoCameraIcon className="w-4 h-4 text-purple-600" />
              ) : (
                <MicrophoneIcon className="w-4 h-4 text-green-600" />
              )}
              <span className="text-xs font-medium text-gray-700">
                {answerFormat.charAt(0).toUpperCase() + answerFormat.slice(1)} Response
              </span>
            </div>
          )}
        </div>

        {/* Question Text */}
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6 leading-relaxed">
          {question?.question_text || question?.text}
        </h2>

        {/* Answer Input - Text Mode */}
        {answerFormat === 'text' && (
          <div className="space-y-4">
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Type your answer here..."
                disabled={isSubmitting || timerExpired}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all resize-none"
                style={{ minHeight: '200px' }}
              />
              
              {/* Character count */}
              <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                {answer.length} characters
              </div>
            </div>

            {/* Contextual tip */}
            {answer.length === 0 && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <div className="flex items-start space-x-2">
                  <SparklesIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <strong>Tip:</strong> Use the STAR method - describe the Situation, Task, Action, and Result.
                  </div>
                </div>
              </div>
            )}

            {/* Privacy disclosure — shown on last question */}
            {isLastQuestion && (
              <p className="mt-4 text-xs text-gray-400 leading-relaxed">
                By submitting, your responses may be used by{' '}
                <span className="text-gray-500">{companyName}</span>{' '}
                for employment-related purposes, including onboarding, surveys, or feedback requests.
              </p>
            )}

            {/* Submit button */}
            {canSubmit && (
              <div className="flex justify-end">
                <button
                  onClick={handleSubmitAnswer}
                  disabled={isSubmitting}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-5 h-5" />
                      <span>Submit Answer</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Media Recorder - Video/Audio */}
      {(answerFormat === 'video' || answerFormat === 'audio') && (
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <MediaRecorder
            key={`media-${question?.id}`}
            mode={answerFormat}
            onSubmit={handleMediaSubmit}
            autoStart={true}
            permissionAlreadyGranted={mediaPermissionGranted}
            previewOnly={false}
            timerExpired={timerExpired}
          />

          {/* Privacy disclosure — shown on last question */}
          {isLastQuestion && (
            <p className="mt-4 text-xs text-gray-400 leading-relaxed">
              By submitting, your responses may be used by{' '}
              <span className="text-gray-500">{companyName}</span>{' '}
              for employment-related purposes, including onboarding, surveys, or feedback requests.
            </p>
          )}
        </div>
      )}

      {/* Timer Expired Message */}
      {timerExpired && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
          <p className="text-sm text-red-700 font-medium">
            Time is up! Your response has been automatically submitted.
          </p>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;